import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  IconButton,
  makeStyles,
  Snackbar,
  SnackbarProps,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { ApplicationState } from '../store';
import { useDispatch, useSelector } from 'react-redux';
import { notificationActions } from '../store/notification/actions';
import { primaryColor, greenColor, warningColor } from '../theme/theme';
import { MessageType } from '../store/notification/types';

const useStyles = makeStyles(() => ({
  button: {
    color: primaryColor(),
    fontWeight: 'bold',
  },
  info: {
    borderColor: primaryColor(),
  },
  success: {
    borderColor: greenColor,
  },
  error: {
    borderColor: warningColor,
  },
  snackbar: {
    marginBottom: `env(safe-area-inset-bottom)`,
  },
}));

const MessageSnackbar: React.FC<SnackbarProps> = (props) => {
  const { t } = useTranslation(['translations']);

  const dispatch = useDispatch();

  const { ...otherProps } = props;

  const classes = useStyles();

  const message = useSelector(
    (state: ApplicationState) => state.notification.messages[0],
  );

  if (!message) {
    return <></>;
  }

  const handleClose = (
    _event: React.SyntheticEvent | React.MouseEvent,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }

    dispatch(notificationActions.clearMessage(message.id));
  };

  const onCallbackClick = () => {
    if (message.actionCallback) {
      message.actionCallback();
    }

    dispatch(notificationActions.clearMessage(message.id));
  };

  const snackbarClasses = [classes.snackbar];

  if (otherProps.className) {
    snackbarClasses.push(otherProps.className);
  }

  switch (message.type) {
    case MessageType.Info:
      snackbarClasses.push(classes.info);
      break;
    case MessageType.Success:
      snackbarClasses.push(classes.success);
      break;
    case MessageType.Error:
      snackbarClasses.push(classes.error);
      break;
  }

  const snackbarClass = snackbarClasses.join(' ');

  const messageText =
    message.isTextTranslationKey && message.text
      ? t(message.text, message.textTranslationKeyOptions)
      : message.text;

  const actionText =
    message.isTextTranslationKey && message.actionText
      ? t(message.actionText, message.textTranslationKeyOptions)
      : message.actionText;

  return (
    <Snackbar
      {...otherProps}
      key={message.id}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      open={true}
      autoHideDuration={
        message.autoClose ? message.hideDuration ?? 5000 : undefined
      }
      onClose={handleClose}
      message={messageText}
      action={
        <React.Fragment>
          {actionText && message.actionCallback && (
            <Button
              color="secondary"
              size="small"
              onClick={onCallbackClick}
              className={classes.button}
            >
              {message.actionText}
            </Button>
          )}
          {message.manualCloseEnabled && (
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleClose}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </React.Fragment>
      }
      className={snackbarClass}
    />
  );
};

export { MessageSnackbar };
